import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../components/common/Icon';
import { icons } from '../../config/icons';
import { useAuth } from '../../context/AuthContext';

const Reschedule = () => {
    const { setHeader, setFooter } = useOutletContext();
    const { settings } = useAuth(); // Assuming AuthContext is available to get logo

    // Hide header and footer for standalone view
    useEffect(() => { 
        setHeader(false); 
        setFooter(false); 
        return () => { setHeader(true); setFooter(true); }; 
    }, [setHeader, setFooter]);

    const { token } = useParams();
    const navigate = useNavigate();
    const [proposedTimes, setProposedTimes] = useState([{ date: '', time: '' }]);
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    const handleAddTime = () => {
        if (proposedTimes.length < 3) {
            setProposedTimes([...proposedTimes, { date: '', time: '' }]);
        }
    };

    const handleRemoveTime = (index) => {
        const newTimes = [...proposedTimes];
        newTimes.splice(index, 1);
        setProposedTimes(newTimes);
    };

    const handleChange = (index, field, value) => {
        const newTimes = [...proposedTimes];
        newTimes[index][field] = value;
        setProposedTimes(newTimes);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const isValid = proposedTimes.every(t => t.date && t.time);
        if (!isValid) {
            setErrorMessage('Please fill in all date and time fields.');
            return;
        }

        setStatus('loading');
        setErrorMessage('');

        try {
            await axios.post('/api/careers/reschedule', {
                token,
                proposed_times: proposedTimes
            });
            setStatus('success');
        } catch (error) {
            setStatus('error');
            setErrorMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
        }
    };

    if (status === 'success') {
        return (
            <div className='login-page'>
                <div className='login-container'>
                    <div className='login-logo'> 
                        <img src={settings?.logo ? `/defaults/logo/${settings.logo}` : '/defaults/no-image.webp'} alt='Logo' /> 
                        <span><h2>{settings?.system || 'Vital Solution Partners'}</h2><p>Recruitment System</p></span> 
                    </div>
                    <div className='pagelet'>
                        <div className='login-body'>
                            <div className='login-head' style={{ textAlign: 'center' }}>
                                <Icon icon={icons.check} style={{ fontSize: '3rem', color: 'var(--success)', marginBottom: '10px' }} />
                                <h2>Request Submitted</h2> 
                                <p style={{ marginTop: '10px' }}>Your reschedule request has been successfully sent to the recruitment team. We will review your proposed times and get back to you shortly.</p> 
                            </div>
                            <div className='login-actions' style={{ justifyContent: 'center', marginTop: '20px' }}>
                                <button type="button" className="button" onClick={() => navigate('/')}> Back to Home </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='login-page'>
            <div className='login-container'>
                <div className='login-logo'> 
                    <img src={settings?.logo ? `/defaults/logo/${settings.logo}` : '/defaults/no-image.webp'} alt='Logo' /> 
                    <span><h2>{settings?.system || 'Vital Solution Partners'}</h2><p>Recruitment System</p></span> 
                </div>
                <div className='pagelet'>
                    <div className='login-body'>
                        <div className='login-head'> 
                            <h2>Request Reschedule</h2> 
                            <p>Please propose up to 3 alternate dates and times for your interview.</p> 
                        </div>
                        
                        <form className='form-case' onSubmit={handleSubmit}>
                            {errorMessage && (<p className='invalid' style={{ marginBottom: '15px' }}>{errorMessage}</p>)}
                            
                            {proposedTimes.map((timeObj, index) => (
                                <div key={index} style={{ padding: '15px', border: '1px solid var(--border)', borderRadius: '5px', marginBottom: '15px', position: 'relative', backgroundColor: 'var(--surface-color, #fff)' }}>
                                    <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>Option {index + 1}</h4>
                                    
                                    {proposedTimes.length > 1 && (
                                        <button 
                                            type="button" 
                                            onClick={() => handleRemoveTime(index)} 
                                            style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '5px' }}
                                            title="Remove Option"
                                        >
                                            <Icon icon={icons.close} />
                                        </button>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div className='input-case' style={{ margin: 0 }}>
                                            <p>Date</p>
                                            <div className='input-group left'>
                                                <input 
                                                    type="date" 
                                                    value={timeObj.date}
                                                    onChange={(e) => handleChange(index, 'date', e.target.value)}
                                                    required 
                                                    disabled={status === 'loading'}
                                                />
                                                <Icon icon={icons.calendar} className="input-icon" />
                                            </div>
                                        </div>
                                        <div className='input-case' style={{ margin: 0 }}>
                                            <p>Time</p>
                                            <div className='input-group left'>
                                                <input 
                                                    type="time" 
                                                    value={timeObj.time}
                                                    onChange={(e) => handleChange(index, 'time', e.target.value)}
                                                    required 
                                                    disabled={status === 'loading'}
                                                />
                                                <Icon icon={icons.clock} className="input-icon" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {proposedTimes.length < 3 && (
                                <div className='login-actions' style={{ justifyContent: 'center', marginBottom: '20px' }}>
                                    <button type="button" className="button outline" onClick={handleAddTime} disabled={status === 'loading'} style={{ width: '100%', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <Icon icon={icons.addSimple} /> Add Another Option
                                    </button>
                                </div>
                            )}

                            <div className='login-actions'>
                                <button type="submit" className="button" disabled={status === 'loading'} style={{ width: '100%', justifyContent: 'center' }}> 
                                    {status === 'loading' ? 'Submitting...' : 'Submit Request'} 
                                </button>
                                <button type="button" className="button outline" onClick={() => navigate('/')} style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reschedule;

